<template>
    <!-- Image upload state -->
    <div
        v-if="!loading && (showImageUpload || !trip.image)"
        class="mb-8 relative w-full h-[416px] rounded-lg"
    >
        <TripImageUpload
            @image-selected="$emit('image-selected', $event)"
            @save="$emit('save')"
            :query="tripDestination.valueForSearch"
            :showConfirmation="true"
            :height="400"
            :trip="trip"
            :image="tripImage"
        />
        <button
            v-if="trip.image"
            class="absolute top-6 right-6 border-none w-7.5 h-7.5 flex items-center justify-center text-sm cursor-pointer bg-slate-100/50 backdrop-blur px-3 py-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700/75 transition-all text-black gap-2"
            @click="$emit('close-upload')"
        >
            <Icon icon="close" />
            <span>Cancel</span>
        </button>
        <transition-group
            tag="div"
            enter-active-class="transition ease-out duration-300"
            enter-from-class="transform opacity-0"
            enter-to-class="transform opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="transform opacity-100"
            leave-to-class="transform opacity-0"
        >
            <div
                class="absolute bottom-0 left-0 right-0 flex flex-wrap justify-between pt-4 pb-2 text-white bg-gradient-to-t from-gray-500 to-transparent via-gray-500/50 rounded-lg z-10 md:flex transition-all"
                v-if="!loading"
            >
                <TripStatsOverlay :trip="trip" variant="desktop" />
            </div>
        </transition-group>
    </div>

    <!-- Regular cover image -->
    <div
        v-else
        class="bg-no-repeat bg-center bg-cover mb-6 md:mb-8 relative w-full h-[200px] sm:h-[300px] md:h-[416px] rounded-lg"
        :style="`background-image: url(${tripImage})`"
    >
        <transition-group
            tag="div"
            enter-active-class="transition ease-out duration-300"
            enter-from-class="transform opacity-0"
            enter-to-class="transform opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="transform opacity-100"
            leave-to-class="transform opacity-0"
        >
            <div
                class="absolute bottom-0 left-0 right-0 flex flex-wrap justify-between pt-4 text-white bg-gradient-to-t from-gray-500 to-transparent via-gray-500/50 rounded-lg z-10 md:flex transition-all"
                v-if="!loading"
            >
                <TripStatsOverlay :trip="trip" variant="desktop" />
            </div>
        </transition-group>

        <!-- Edit/Save buttons -->
        <button
            class="absolute bottom-4 right-4 w-10 h-10 rounded bg-white dark:bg-slate-800 z-20"
            @click="showOptions = !showOptions"
            v-click-outside="() => (showOptions = false)"
        >
            <Icon icon="edit" />
        </button>
        <button
            class="absolute bottom-4 right-16 w-10 h-10 rounded bg-white dark:bg-slate-800 z-20"
            v-if="isUpdating"
            @click="$emit('save')"
            title="Save changes"
        >
            <Icon icon="check" :height="16" :width="16" />
        </button>

        <!-- Options dropdown -->
        <transition name="fade">
            <div class="absolute top-full right-5 z-10" v-if="showOptions">
                <div class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow dark:shadow-slate-900/50">
                    <ul class="text-sm">
                        <li>
                            <button
                                class="px-2 py-1 text-left flex items-center gap-2"
                                @click="$emit('regenerate')"
                            >
                                <Icon icon="rotate" />
                                <span>Regenerate cover</span>
                            </button>
                        </li>
                        <li>
                            <button
                                class="px-2 py-1 text-left flex items-center gap-2"
                                @click="$emit('open-upload')"
                            >
                                <Icon icon="upload" :width="16" :height="16" />
                                <span>Upload</span>
                            </button>
                        </li>
                        <li v-if="hasTripImage">
                            <button
                                class="px-2 py-1 text-left flex items-center gap-2"
                                @click="$emit('delete-cover')"
                            >
                                <Icon icon="trash" :width="16" :height="16" />
                                <span>Remove</span>
                            </button>
                        </li>
                        <li v-if="isUpdating">
                            <button
                                class="px-2 py-1 text-left flex items-center gap-2"
                                @click="$emit('cancel-changes')"
                            >
                                <Icon icon="reset" :width="16" :height="16" />
                                <span>Cancel</span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import TripImageUpload from '@/components/trips/trip-image-upload.vue';
import TripStatsOverlay from './TripStatsOverlay.vue';

const showOptions = ref(false);

defineProps({
    trip: { type: Object, required: true },
    tripImage: { type: String, default: null },
    showImageUpload: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    isUpdating: { type: Boolean, default: false },
    hasTripImage: { type: Boolean, default: false },
    tripDestination: { type: Object, default: () => ({}) },
});

defineEmits([
    'image-selected',
    'save',
    'close-upload',
    'open-upload',
    'regenerate',
    'cancel-changes',
    'delete-cover',
]);
</script>
